/*
 * Settings, read first and edit second: each card shows what is set, with its own Edit. Sections a role cannot use are
 * left out. Values a location did not set itself come from the brand or HQ, and say so.
 */
// Business info: name, address, time zone, sub locations.
import { oneLine, subLocationName } from "@steward/shared";
import { type ReactNode, useState } from "react";
import { useSettings, useUpdateCompany } from "../../api/hooks";
import { useSession } from "../../app/session";
import { Combobox, InputText } from "../../components/fields";
import { Modal, useToast } from "../../components/overlays";
import { Banner, Button, Card, Details } from "../../components/parts";
import { AddressFields, draftOf, partsOf, subLocationLine, SubLocationSheet } from "../../components/SubLocations";
import { BRAND_COLORS, BrandEdit, BrandLinksCard, builtIn } from "./Brand";
import { errorText } from "./common";

const ZONES = [
  ["America/Chicago", "Central (New Orleans, Chicago)"],
  ["America/New_York", "Eastern (New York, Atlanta)"],
  ["America/Denver", "Mountain (Denver)"],
  ["America/Phoenix", "Arizona (Phoenix)"],
  ["America/Los_Angeles", "Pacific (Los Angeles)"],
  ["America/Anchorage", "Alaska"],
  ["Pacific/Honolulu", "Hawaii"],
] as const;

const zoneName = (tz: string) => ZONES.find(([z]) => z === tz)?.[1] ?? tz;

/**
 * A location (or franchise) can't change its own name or phone; the brand or HQ sets them. An owner of a company above it
 * still can (the server checks the same rule).
 */
function useNameLocked(): boolean {
  const { company, companies } = useSession();
  if (company.level !== "location") return false;
  for (let up = company.parentId; up;) {
    const above = companies.find((c) => c.id === up);
    if (!above) break;
    if (above.role === "owner") return false;
    up = above.parentId;
  }
  return true;
}

export function BusinessInfo() {
  const { company, companies, can } = useSession();
  const [editing, setEditing] = useState<"info" | "brand" | null>(null);
  const editable = can("company.update");
  const locked = useNameLocked();
  // Brand color belongs to the brand (a company with locations below it); locations take it from there.
  const isBrand = companies.some((c) => c.parentId === company.id) || company.level !== "location";
  const edit = (what: "info" | "brand") =>
    editable ? (
      <Button type="secondary" size="small" onClick={() => setEditing(what)}>
        Edit
      </Button>
    ) : null;
  return (
    <>
      <Card id="info" title="Business info" action={edit("info")} flush>
        <Details
          rows={[
            ["Name", company.name],
            ["Phone", company.phone],
            ["Email", company.email],
            ["Address", oneLine(companyParts(company)) || null],
            ["Time zone", zoneName(company.timezone)],
          ]}
        />
        {locked && editable ? (
          <p className="muted small card-note">
            The brand sets the name, phone and email for every location. Ask them to change it.
          </p>
        ) : null}
      </Card>
      {isBrand ? (
        <Card id="brand" title="Brand" action={edit("brand")} flush>
          <Details
            rows={[
              ...BRAND_COLORS.map(([key, label]): [string, ReactNode] => {
                const c = company[key];
                return [
                  label,
                  c ? (
                    <>
                      <span className="swatch" style={{ background: c }} />
                      {c.toUpperCase()}
                    </>
                  ) : key === "brandColor" ? null : (
                    <span className="muted">{builtIn(company.trade, key)} built in</span>
                  ),
                ];
              }),
              ["Web address", company.appUrl ?? `${company.linkUrl} (from above)`],
              [
                "Client web address",
                company.clientUrl ??
                  (company.clientLinkUrl === company.linkUrl ? "Same as web address" : `${company.clientLinkUrl} (from above)`),
              ],
              [
                "Logo",
                company.logoUrl ? (
                  <span className="logo-on-brand" style={{ background: "#fff" }}>
                    <img src={company.logoUrl} alt="" />
                  </span>
                ) : null,
              ],
              [
                "White logo",
                company.logoWhiteUrl ? (
                  <span
                    className="logo-on-brand"
                    style={{ background: company.accentColor ?? builtIn(company.trade, "accentColor") }}
                  >
                    <img src={company.logoWhiteUrl} alt="" />
                  </span>
                ) : null,
              ],
            ]}
          />
          <p className="muted" style={{ padding: "0 16px 16px" }}>
            Every location of {company.name} uses these. The accent, secondary and call to action colors and the white logo are
            what clients see on pay and quote pages, emails and invoices. Pay links, quotes and the client hub open at the web
            address.
          </p>
        </Card>
      ) : null}
      {company.level === "location" ? <SubLocationsCard canEdit={!locked && editable} /> : null}
      <BrandLinksCard />
      {!editable ? <Banner>Only the owner can change business info. Ask the owner if something is wrong.</Banner> : null}
      {editing === "info" ? <BusinessEdit onClose={() => setEditing(null)} /> : null}
      {editing === "brand" ? <BrandEdit onClose={() => setEditing(null)} /> : null}
    </>
  );
}

function BusinessEdit({ onClose }: { onClose: () => void }) {
  const { company } = useSession();
  const locked = useNameLocked();
  const save = useUpdateCompany();
  const toast = useToast();
  const [f, setF] = useState({
    name: company.name,
    phone: company.phone ?? "",
    email: company.email ?? "",
    timezone: company.timezone,
  });
  const [where, setWhere] = useState(draftOf(companyParts(company)));
  const [error, setError] = useState<string | null>(null);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const submit = () => {
    setError(null);
    const { parts, problem } = partsOf(where);
    if (problem) {
      setError(problem);
      return;
    }
    save.mutate(
      {
        id: company.id,
        patch: {
          // A location's name, phone and email are the brand's to change, so they are never sent from here.
          ...(locked ? {} : { name: f.name, phone: f.phone || null, email: f.email || null }),
          // The address in parts, like a client's (owner rule, 2026-11-02).
          address: parts?.street1 ?? null,
          address2: parts?.street2 ?? null,
          city: parts?.city ?? null,
          state: parts?.state ?? null,
          zip: parts?.zip ?? null,
          timezone: f.timezone,
        },
      },
      {
        onSuccess: () => {
          toast("Business info saved");
          onClose();
        },
        onError: (e) => setError(errorText(e)),
      },
    );
  };
  return (
    <Modal
      title="Edit business info"
      open
      onClose={onClose}
      footer={
        <>
          <Button type="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={save.isPending} onClick={submit}>
            Save
          </Button>
        </>
      }
    >
      {error ? <Banner type="error">{error}</Banner> : null}
      <InputText
        label="Name"
        value={f.name}
        onChange={set("name")}
        required={!locked}
        disabled={locked}
        description={locked ? "The brand sets this for every location." : undefined}
      />
      <div className="form-grid">
        <InputText
          label="Phone"
          type="tel"
          value={f.phone}
          onChange={set("phone")}
          disabled={locked}
          description={locked ? "The brand sets this too." : undefined}
        />
        <InputText
          label="Email"
          type="email"
          value={f.email}
          onChange={set("email")}
          disabled={locked}
          description={locked ? "The brand sets this too." : undefined}
        />
      </div>
      <AddressFields value={where} onChange={setWhere} />
      <Combobox
        label="Time zone"
        value={f.timezone}
        onChange={(v) => setF({ ...f, timezone: v })}
        options={ZONES.map(([value, label]) => ({ value, label }))}
        description="Quiet hours and 'today' use this."
      />
    </Modal>
  );
}

/** A company's address parts (address is Street 1). */
function companyParts(c: {
  address: string | null;
  address2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
}) {
  return {
    street1: c.address ?? "",
    ...(c.address2 ? { street2: c.address2 } : {}),
    city: c.city ?? "",
    state: c.state ?? "",
    zip: c.zip ?? "",
  };
}

/**
 * A location's sub locations (real branches like The Cleaners of Metairie), so everyone working here knows their name,
 * phone, email, address and ZIPs. The brand or head office sets them; an owner above can change them from here too.
 * The location's own people and its franchisee only see them.
 */
function SubLocationsCard({ canEdit }: { canEdit: boolean }) {
  const { company, companies } = useSession();
  const s = useSettings(company.id);
  const [open, setOpen] = useState<number | null>(null);
  const subs = s.data?.settings.messaging.locationLines ?? [];
  let brand = company.name;
  for (let up = company.parentId; up;) {
    const above = companies.find((c) => c.id === up);
    if (!above) break;
    if (above.level === "brand") brand = above.name;
    up = above.parentId;
  }
  return (
    <Card
      id="sub-locations"
      title="Sub locations"
      action={
        canEdit ? (
          <Button type="secondary" size="small" onClick={() => setOpen(-1)}>
            Add
          </Button>
        ) : null
      }
      flush
    >
      {subs.length ? (
        <Details
          rows={subs.map((x, i): [string, ReactNode] => [
            subLocationName(brand, x.name),
            canEdit ? (
              <button type="button" className="link" onClick={() => setOpen(i)}>
                {subLocationLine(x)}
              </button>
            ) : (
              subLocationLine(x)
            ),
          ])}
        />
      ) : (
        <p className="muted card-note">No sub locations.</p>
      )}
      {!canEdit ? (
        <p className="muted small card-note">The brand or head office sets sub locations. Ask them to change one.</p>
      ) : null}
      {open !== null ? (
        <SubLocationSheet
          location={{ id: company.id, name: company.name }}
          brand={brand}
          subs={subs}
          index={open}
          onClose={() => setOpen(null)}
        />
      ) : null}
    </Card>
  );
}
